import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DynamicIcon from "../DynamicIcon";
import {
    selectLeadDetails,
    selectLeadDetailsLoading,
    resetLeadDetails
} from "../../redux/leads/leadsSlice";
import { closeModal } from "../../redux/rightModalSlice";
import LoadingSection from "../LoadingSection";

export default function ViewLead() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const lead = useSelector(selectLeadDetails);
    const leadLoading = useSelector(selectLeadDetailsLoading);

    useEffect(() => {
        return () => {
            dispatch(resetLeadDetails());
        };
    }, [dispatch]);

    if (leadLoading) {
        return <LoadingSection />;
    }

    if (!lead) {
        return (
            <div className="p-6">
                <h2 className="text-xl font-bold mb-4">Lead non trovata</h2>
                <p className="text-gray-600">La lead richiesta non è disponibile.</p>
                <div className="mt-4">
                    <button className="secondary" onClick={() => dispatch(closeModal())}>
                        <DynamicIcon iconClass="fas fa-arrow-left" />
                        Torna indietro
                    </button>
                </div>
            </div>
        );
    }

    const getLeadDisplayName = () => {
        if (lead.business) {
            return lead.company || '--';
        }
        return [lead.name, lead.surname].filter(Boolean).join(' ') || '--';
    };

    const formatDate = (date) => {
        if (!date) return '--';
        return new Date(date).toLocaleDateString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const statusColor = lead.statusColor || '#6B7280';

    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-2xl font-bold">{getLeadDisplayName()}</h2>
                    <p className="text-sm text-gray-500">{lead.code}</p>
                </div>
                <span
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                    style={{ backgroundColor: statusColor + '20', color: statusColor }}
                >
                    {lead.statusLabel || 'N/D'}
                </span>
            </div>

            {/* Conversione avvenuta */}
            {lead.convertedToCustomerId && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-6">
                    <p className="text-sm text-green-700">
                        <DynamicIcon iconClass="fas fa-check-circle" /> Convertita in cliente il {formatDate(lead.convertedDate)}
                    </p>
                </div>
            )}

            {/* Cliente/Prospect Collegato */}
            {lead.linkedCustomerId && (
                <div className="bg-white shadow-md rounded-lg p-4 mb-6">
                    <h3 className="text-lg font-semibold mb-3">Collegamento</h3>
                    <div className="flex items-center gap-2">
                        <DynamicIcon iconClass={lead.linkedCustomerIsProspect ? "fi fi-br-user-add" : "fa-solid fa-user-check"} />
                        <span className="text-base font-medium">{lead.linkedCustomerLabel}</span>
                        {lead.linkedCustomerIsProspect ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                                Prospect
                            </span>
                        ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                Cliente
                            </span>
                        )}
                    </div>
                </div>
            )}

            {/* Impianto Collegato */}
            {lead.linkedMaintenanceId && (
                <div className="bg-white shadow-md rounded-lg p-4 mb-6">
                    <h3 className="text-lg font-semibold mb-3">Impianto Collegato</h3>
                    <div className="flex items-center gap-2">
                        <DynamicIcon iconClass="fa-solid fa-industry" />
                        <span className="text-base font-medium">{lead.linkedMaintenanceLabel || '--'}</span>
                    </div>
                </div>
            )}

            {/* Contatti e Indirizzo */}
            {(lead.phone || lead.email || lead.address) && (
                <div className="bg-white shadow-md rounded-lg p-4 mb-6">
                    <h3 className="text-lg font-semibold mb-3">Contatti</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {lead.phone && (
                            <div>
                                <p className="text-sm font-medium text-gray-500">Telefono</p>
                                <p className="text-base">{lead.phone}</p>
                            </div>
                        )}
                        {lead.email && (
                            <div>
                                <p className="text-sm font-medium text-gray-500">Email</p>
                                <p className="text-base">{lead.email}</p>
                            </div>
                        )}
                        {lead.address && (
                            <div>
                                <p className="text-sm font-medium text-gray-500">Indirizzo</p>
                                <p className="text-base">{lead.address}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Dati Generali */}
            <div className="bg-white shadow-md rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold mb-3">Dati Generali</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <p className="text-sm font-medium text-gray-500">Data Creazione</p>
                        <p className="text-base">{formatDate(lead.creationDate)}</p>
                    </div>
                </div>
            </div>

            {/* Collaboratori Assegnati */}
            <div className="bg-white shadow-md rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold mb-3">Collaboratori Assegnati</h3>
                {lead.assignedUsers && lead.assignedUsers.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                        {lead.assignedUsers.map((u, index) => (
                            <span
                                key={index}
                                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                                style={{
                                    backgroundColor: (u.userColor || '#6B7280') + '20',
                                    color: u.userColor || '#6B7280'
                                }}
                            >
                                <DynamicIcon iconClass="fa-solid fa-user" />
                                <span className="ml-1">{u.userLabel}</span>
                            </span>
                        ))}
                    </div>
                ) : (
                    <p className="text-base text-gray-400">Nessun collaboratore assegnato</p>
                )}
            </div>

            {/* Descrizione */}
            <div className="bg-white shadow-md rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold mb-3">Descrizione</h3>
                <p className="text-base whitespace-pre-wrap">{lead.description || "--"}</p>
            </div>

            {/* Note */}
            {lead.notes && (
                <div className="bg-white shadow-md rounded-lg p-4 mb-6">
                    <h3 className="text-lg font-semibold mb-3">Note</h3>
                    <p className="text-base whitespace-pre-wrap">{lead.notes}</p>
                </div>
            )}

            {/* Bottone Crea Preventivo (visibile solo per lead vinte) */}
            {lead.statusCode === 'vinta' && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-purple-700">
                                <DynamicIcon iconClass="fa-solid fa-file-invoice-dollar" /> Lead vinta — crea un preventivo
                            </p>
                        </div>
                        <button
                            className="primary"
                            onClick={() => {
                                dispatch(closeModal());
                                navigate('/quotationsList', {
                                    state: {
                                        openNewQuotation: true,
                                        prefillFromLead: lead
                                    }
                                });
                            }}
                        >
                            <DynamicIcon iconClass="fa-solid fa-plus" />
                            Crea Preventivo
                        </button>
                    </div>
                    {lead.quotationCode && (
                        <p className="text-xs text-purple-600 mt-2">
                            Preventivo già collegato: <strong>{lead.quotationCode}</strong>
                        </p>
                    )}
                </div>
            )}

            {/* Bottone Chiudi */}
            <div className="flex justify-end">
                <button className="secondary" onClick={() => dispatch(closeModal())}>
                    <DynamicIcon iconClass="fas fa-times" />
                    Chiudi
                </button>
            </div>
        </div>
    );
}
